import { store } from "../../app/store";
import { isReadOnlyPreviewAllowed } from "../../config/env";
import { RoleModuleAccessLevel } from "../rolesPermissions/rolesPermissions.types";
import { ADMIN_MODULE_MATCHERS } from "./adminModules";
import type { AccessedModule, User } from "./auth.types";

/**
 * Login `accessedModules[].permissions` hierarchy (single string value):
 *   READ < READ_WRITE < APPROVE
 * APPROVE is a strict superset of READ_WRITE (read + write + approve).
 */
export type ParsedModuleAccessLevel =
	| RoleModuleAccessLevel.READ
	| RoleModuleAccessLevel.READ_WRITE
	| RoleModuleAccessLevel.APPROVE;

/** Local-dev header preview level; null when preview is not available (non-local). */
function getMockAccessPreview(): ParsedModuleAccessLevel | null {
	if (!isReadOnlyPreviewAllowed()) return null;
	const preview = store.getState().auth.mockAccessPreview;
	if (preview === RoleModuleAccessLevel.READ) return RoleModuleAccessLevel.READ;
	if (preview === RoleModuleAccessLevel.APPROVE) {
		return RoleModuleAccessLevel.APPROVE;
	}
	return RoleModuleAccessLevel.READ_WRITE;
}

export function normalizeModuleLabel(value: string): string {
	return value
		.trim()
		.toLowerCase()
		.replace(/&/g, "and")
		.replace(/_/g, " ")
		.replace(/\s+/g, " ");
}

/** Parse login permission string into a known access level (exact match preferred). */
export function parseModuleAccessLevel(
	permissions: string,
): ParsedModuleAccessLevel | null {
	const normalized = permissions.trim().toUpperCase();
	if (!normalized) return null;

	if (
		normalized === RoleModuleAccessLevel.APPROVE ||
		normalized === "APPROVER"
	) {
		return RoleModuleAccessLevel.APPROVE;
	}
	if (
		normalized === RoleModuleAccessLevel.READ_WRITE ||
		normalized === "WRITE"
	) {
		return RoleModuleAccessLevel.READ_WRITE;
	}
	if (normalized === RoleModuleAccessLevel.READ) {
		return RoleModuleAccessLevel.READ;
	}

	// Legacy / compound strings — check higher levels before READ (substring of READ_WRITE)
	if (normalized.includes("APPROVE")) return RoleModuleAccessLevel.APPROVE;
	if (normalized.includes("READ_WRITE") || normalized.includes("WRITE")) {
		return RoleModuleAccessLevel.READ_WRITE;
	}
	if (normalized.includes("READ")) return RoleModuleAccessLevel.READ;
	return null;
}

function permissionIncludesRead(permissions: string): boolean {
	return parseModuleAccessLevel(permissions) != null;
}

function permissionIncludesWrite(permissions: string): boolean {
	const level = parseModuleAccessLevel(permissions);
	return (
		level === RoleModuleAccessLevel.READ_WRITE ||
		level === RoleModuleAccessLevel.APPROVE
	);
}

function permissionIncludesApprove(permissions: string): boolean {
	return parseModuleAccessLevel(permissions) === RoleModuleAccessLevel.APPROVE;
}

export function mapAccessedModule(raw: unknown): AccessedModule | null {
	const row = (raw ?? {}) as Record<string, unknown>;
	const moduleId = String(row.moduleId ?? "");
	const moduleName = String(row.moduleName ?? "");
	if (!moduleId && !moduleName) return null;
	return {
		moduleId,
		moduleName,
		code: typeof row.code === "string" ? row.code : undefined,
		permissions: String(row.permissions ?? ""),
	};
}

export function moduleMatchesKey(
	module: AccessedModule,
	moduleKey: string,
): boolean {
	const config = ADMIN_MODULE_MATCHERS.find((entry) => entry.key === moduleKey);
	const moduleCode = (module.code ?? "").toUpperCase();
	const moduleName = normalizeModuleLabel(module.moduleName);

	if (config) {
		if (config.codes.some((code) => code === moduleCode)) return true;
		if (
			config.names.some((name) => normalizeModuleLabel(name) === moduleName)
		) {
			return true;
		}
	}

	return normalizeModuleLabel(moduleKey) === moduleName;
}

function userHasModulePermission(
	user: User | null,
	moduleKey: string,
	includes: (permissions: string) => boolean,
): boolean {
	if (!user) return false;
	const modules = user.accessedModules ?? [];
	if (modules.length === 0) return false;
	return modules.some(
		(module) =>
			moduleMatchesKey(module, moduleKey) && includes(module.permissions),
	);
}

export function hasModuleReadAccess(
	user: User | null,
	moduleKey: string,
): boolean {
	return userHasModulePermission(user, moduleKey, permissionIncludesRead);
}

/** True for READ_WRITE and APPROVE (APPROVE is a superset of READ_WRITE). */
export function hasModuleWriteAccess(
	user: User | null,
	moduleKey: string,
): boolean {
	const preview = getMockAccessPreview();
	if (preview === RoleModuleAccessLevel.READ) return false;
	if (preview === RoleModuleAccessLevel.APPROVE) {
		// Local Approver preview: APPROVE includes write for modules the user can open.
		return userHasModulePermission(user, moduleKey, permissionIncludesRead);
	}
	// READ_WRITE preview (or non-local): use login permissions.
	return userHasModulePermission(user, moduleKey, permissionIncludesWrite);
}

/** True only for APPROVE — use for approve/reject actions once modules adopt it. */
export function hasModuleApproveAccess(
	user: User | null,
	moduleKey: string,
): boolean {
	const preview = getMockAccessPreview();
	if (preview === RoleModuleAccessLevel.READ) return false;
	if (preview === RoleModuleAccessLevel.READ_WRITE) return false;
	if (preview === RoleModuleAccessLevel.APPROVE) {
		return userHasModulePermission(user, moduleKey, permissionIncludesRead);
	}
	return userHasModulePermission(user, moduleKey, permissionIncludesApprove);
}

/** Sidebar visibility: module access from login `accessedModules` only. */
export function canAccessNavItem(
	user: User | null,
	item: { moduleKey?: string },
): boolean {
	if (!user) return true;
	if (!item.moduleKey) return true;
	return hasModuleReadAccess(user, item.moduleKey);
}
